import {useState, useEffect} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {Button} from 'react-bootstrap'
import {getFunctions, httpsCallable } from 'firebase/functions'
import { getAuth, verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import {generatePopup} from '../../../../utils/popup'

const initialValues = {
  newPassword: '',
  confirmPassword: '',
}

const resetPasswordSchema = Yup.object().shape({
  newPassword: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .required('New password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Passwords must match')
    .required('Confirm password is required'),
});

export function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [hasErrors, setHasErrors] = useState<boolean | undefined | any>(undefined);
  const [oobCode, setOobCode] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const functions = getFunctions();
  const auth = getAuth();

  useEffect(() => {
    // Get the oobCode from the URL
    const code = searchParams.get('oobCode');
    if (!code) {
      generatePopup('error', 'Invalid or expired reset link');
      navigate('/auth/forgot-password');
      return;
    }
    verifyPasswordResetCode(auth, code).then((email:any) => {
        if (!email) {
            generatePopup('error', 'Invalid or expired reset link');
            navigate('/auth/forgot-password');
            return;
        }
        setEmail(email);
        setOobCode(code);
    }).catch((error) => {
        generatePopup('error', error.message || 'Error verifying reset link');
        navigate('/auth/forgot-password');
        return;
    });
    
  }, [searchParams, navigate]);

  const formik = useFormik({
    initialValues,
    validationSchema: resetPasswordSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      if (!oobCode) return;

      try {
        setLoading(true);
        setHasErrors(undefined);

        await confirmPasswordReset(auth, oobCode, values.newPassword);
        setHasErrors(false);
        setLoading(false);
        generatePopup('success', 'Password has been reset successfully');
        setTimeout(() => {
            navigate('/auth');
        }, 2000);
        
      } catch (error: any) {
        setHasErrors(true);
        setLoading(false);
        setSubmitting(false);
        setStatus(error.message);
      }
    },
  });

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      onSubmit={formik.handleSubmit}
    >
      <div className='text-start mb-10'>
        <h1 className='text-black fw-bolder mb-3'>Reset Password</h1>
        <div className='text-dark1 fw-semibold fs-6'>
          Enter your new password below.
        </div>
      </div>

      {hasErrors === true && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>
            {formik.status || 'Sorry, looks like there are some errors detected, please try again.'}
          </div>
        </div>
      )}

      <div className='fv-row mb-8'>
        <label className='form-label fw-bolder text-dark1 fs-6'>New Password</label>
        <input
          type='password'
          placeholder='Enter new password'
          {...formik.getFieldProps('newPassword')}
          className={clsx(
            'form-control bg-transparent',
            {'is-invalid': formik.touched.newPassword && formik.errors.newPassword},
            {'is-valid': formik.touched.newPassword && !formik.errors.newPassword}
          )}
        />
        {formik.touched.newPassword && formik.errors.newPassword && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.newPassword}</span>
            </div>
          </div>
        )}
      </div>

      <div className='fv-row mb-8'>
        <label className='form-label fw-bolder text-dark1 fs-6'>Confirm Password</label>
        <input
          type='password'
          placeholder='Confirm new password'
          {...formik.getFieldProps('confirmPassword')}
          className={clsx(
            'form-control bg-transparent',
            {'is-invalid': formik.touched.confirmPassword && formik.errors.confirmPassword},
            {'is-valid': formik.touched.confirmPassword && !formik.errors.confirmPassword}
          )}
        />
        {formik.touched.confirmPassword && formik.errors.confirmPassword && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.confirmPassword}</span>
            </div>
          </div>
        )}
      </div>

      <div className='d-flex flex-wrap pb-lg-0'>
        <Button
          type='submit'
          className='btn btn-primary w-100'
          disabled={loading || !oobCode}
        >
          <span className='indicator-label'>Reset Password</span>
          {loading && (
            <span className='indicator-progress'>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </Button>
      </div>
    </form>
  );
}